import useEstimateCreation from "features/subscriptions/datahooks/useEstimateCreation";
import useEstimateUpdate from "features/subscriptions/datahooks/useEstimateUpdate";
import type {
  BillingEstimate,
  InvoiceEstimate,
} from "features/subscriptions/types";
import { getCouponUpdateState } from "features/subscriptions/utils";

import type { Pricing } from "common/types";

export default function useUpdateOrCreateEstimate({
  pricing,
  quantity,
  subscriptionId,
  isCustomerSubscription,
  couponAction,
  isEnabled = true,
}: {
  pricing: { plan: Pricing; addon: Pricing } | undefined;
  quantity: number;
  subscriptionId: string | undefined;
  isCustomerSubscription: boolean;
  couponAction: string | "remove" | undefined;
  isEnabled?: boolean;
}) {
  const isCreatingNew = !subscriptionId;

  const couponUpdateState = getCouponUpdateState(couponAction);

  const { estimateUpdate, isLoadingEstimateUpdate, estimateUpdateError } =
    useEstimateUpdate({
      pricing,
      quantity,
      isProrated: false,
      subscriptionId: !isCustomerSubscription && subscriptionId,
      isEnabled: isEnabled && !isCreatingNew,
      couponUpdateState,
    });

  const {
    estimateUpdate: proRatedEstimateUpdate,
    isLoadingEstimateUpdate: isLoadingProRatedEstimateUpdate,
    estimateUpdateError: proRatedEstimateUpdateError,
  } = useEstimateUpdate({
    pricing,
    quantity,
    isProrated: true,
    subscriptionId: !isCustomerSubscription && subscriptionId,
    isEnabled: isEnabled && !isCreatingNew,
    couponUpdateState,
  });

  const { estimateCreation, isLoadingEstimateCreation, estimateCreationError } =
    useEstimateCreation({
      isCustomerSubscription,
      pricing,
      quantity,
      isEnabled: isEnabled && isCreatingNew,
      couponId: couponAction && couponAction !== "remove" && couponAction,
    });

  let updateOrCreateEstimate: InvoiceEstimate;
  let toPayNowEstimate: BillingEstimate;

  if (isCreatingNew && estimateCreation) {
    updateOrCreateEstimate =
      estimateCreation.invoice_estimate ||
      estimateCreation.next_invoice_estimate;
    toPayNowEstimate = estimateCreation;
  } else {
    if (estimateUpdate) {
      updateOrCreateEstimate =
        estimateUpdate.invoice_estimate || estimateUpdate.next_invoice_estimate;
    }
    if (proRatedEstimateUpdate) {
      toPayNowEstimate = proRatedEstimateUpdate;
    }
  }

  const isLoadingEstimate = isCreatingNew
    ? isLoadingEstimateCreation
    : isLoadingEstimateUpdate || isLoadingProRatedEstimateUpdate;

  const estimateError = isCreatingNew
    ? estimateCreationError
    : estimateUpdateError || proRatedEstimateUpdateError;

  return {
    toPayNowEstimate,
    updateOrCreateEstimate,
    isLoadingEstimate,
    estimateError,
  };
}
